"use client";

import { ImxRewards } from "@/components/icons/ImxRewards";
import CrossGameModal from "@/components/ui/CrossGameModal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NEXT_IMAGE_QUALITY, QUEST_CAROUSEL_IMAGE_SIZES } from "@/constants";
import { useQuestAnalytics } from "@/hooks/useQuestAnalytics";
import { useWishlistHandler } from "@/hooks/useWishlistHandler";
import { notifyError } from "@/utils/monitoring";
import { Box, HeroCard, lineClamp } from "@biom3/react";
import {
  type LatestEarnings,
  type Quest,
  RuleId,
  getBadgeProps,
} from "@imx-tokens-ts/gems-game/domain";
import { Heart, Loader2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { questMediaURL } from "../utils";
import { MultiLine, MultiLineWithBadge } from "./Multiline";
import { RulesDialog } from "./RulesDialog/RulesDialog";
import {
  type PrimaryTileAction,
  type SecondaryTileAction,
  useGetTileActions,
} from "./actions/tile-actions";
import { regularVariant } from "./utils";

export type QuestTileProps = {
  quest: Quest;
  latestEarnings: LatestEarnings | undefined;
  isPriority?: boolean;
};

export const QuestTile = ({
  quest,
  latestEarnings,
  isPriority = false,
}: QuestTileProps) => {
  const analytics = useQuestAnalytics(quest);
  const router = useRouter();
  const [showRules, setShowRules] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);

  const idPrefix = quest.id;
  const hasCompleted = quest.hasCompleted(latestEarnings);
  const { primaryActions, secondaryActions } = useGetTileActions(
    quest,
    hasCompleted,
  );
  const badgeProps = getBadgeProps({
    startDate: quest.startDate,
    endDate: quest.endDate,
    perputualRewardsQuests: quest.perputualRewardsQuests,
    subtext: quest.subtext,
  });

  const { isWishlisted, addToWishlist, removeFromWishlist, isWishlisting } =
    useWishlistHandler(quest.gameId || "", undefined, undefined, false);

  function handleCompletedQuestButtonClick() {
    if (primaryActions.length > 0 && primaryActions[0].link) {
      router.push(primaryActions[0].link.toString());
      return;
    }
    setShowRules(true);
  }

  function handleButtonClick(
    action: PrimaryTileAction | SecondaryTileAction,
    e?: React.MouseEvent,
  ) {
    e?.stopPropagation();

    if (action.link) {
      return analytics.trackCtaClick({
        control: "label" in action ? action.label : action.action,
        controlType: "Link",
        screen: "QuestTile",
        extras: {
          link: action.link?.toString(),
        },
      });
    }

    setShowRules(true);
    return analytics.trackCtaClick({
      control: "Rules",
      controlType: "Button",
      screen: "QuestTile",
    });
  }

  const handleFollowButtonClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!quest.gameId) return;

    setIsFollowLoading(true);

    try {
      if (isWishlisted) {
        await removeFromWishlist();
      } else {
        await addToWishlist();
      }
    } catch (err) {
      notifyError(err, "wishlist");
    } finally {
      setIsFollowLoading(false);
    }
  };

  const handleTileClick = () => {
    // Prioritize Rules action if it exists
    const hasRulesAction = primaryActions.some(
      (action) => action.action === "Rules",
    );
    if (hasRulesAction) {
      analytics.trackCtaClick({
        control: "Rules",
        controlType: "Tile",
        screen: "QuestTile",
      });
      setShowRules(true);
      return;
    }

    // Otherwise check for link actions
    const linkAction = primaryActions.find((action) => action.link);
    if (linkAction?.link) {
      analytics.trackCtaClick({
        control: "label" in linkAction ? linkAction.label : linkAction.action,
        controlType: "Tile",
        screen: "QuestTile",
        extras: {
          link: linkAction.link?.toString(),
        },
      });

      if (quest.isImmutablePlayLink()) {
        router.push(linkAction.link.toString());
        return;
      }
      window.open(linkAction.link.toString(), "_blank");
      return;
    }
  };

  return (
    <Box
      sx={{
        width: "85%",
        maxWidth: "400px",
        height: "100%",
        flexShrink: 0,
        position: "relative",
      }}
    >
      {quest.gameId && (
        <div className="absolute top-3 right-3 z-10">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleFollowButtonClick}
            disabled={isFollowLoading || isWishlisting}
            className="size-8 md:size-11"
            aria-label={isWishlisted ? "Unfollow game" : "Follow game"}
          >
            {isFollowLoading || isWishlisting ? (
              <Loader2
                className={`size-4 md:size-6 animate-spin ${isWishlisted ? "fill-white" : ""}`}
              />
            ) : (
              <Heart className={`size-8 ${isWishlisted ? "fill-white" : ""}`} />
            )}
          </Button>
        </div>
      )}

      <HeroCard
        testId={`${idPrefix}-tile`}
        size="small"
        onClick={handleTileClick}
        aspectRatio="unset"
        sx={{
          "& > .innerContentContainer": {
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "65%",
            minh: "65%",
          },
          maxWidth: "418px",
          height: "348px",
        }}
      >
        <HeroCard.FramedImage
          imageUrl={questMediaURL(quest.logo).toString()}
          use={
            <Image
              src={questMediaURL(quest.logo).toString()}
              alt={quest.caption}
              width={48}
              height={48}
            />
          }
        />
        {quest.backgroundVideoUrl &&
        quest.backgroundVideoUrl.mimeType !== undefined ? (
          <HeroCard.AssetImage
            testId={`${idPrefix}__video`}
            loading={isPriority ? "eager" : "lazy"}
            use={
              <video
                data-testid={`${idPrefix}__video`}
                src={quest.backgroundVideoUrl.url.toString()}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
                muted
                loop
                autoPlay
                playsInline
                preload={isPriority ? "auto" : "metadata"}
                {...(!isPriority && { loading: "lazy" })}
              />
            }
          />
        ) : (
          <HeroCard.AssetImage
            testId={`${idPrefix}__image`}
            loading={isPriority ? "eager" : "lazy"}
            use={
              <Image
                src={questMediaURL(quest.backgroundMediaUrl).toString()}
                alt={quest.caption}
                fill
                priority={isPriority}
                quality={NEXT_IMAGE_QUALITY}
                sizes={QUEST_CAROUSEL_IMAGE_SIZES}
                style={{ objectPosition: "top" }}
              />
            }
          />
        )}

        <div className="flex flex-col gap-2">
          <HeroCard.Caption testId={`${idPrefix}__caption`}>
            {quest.caption}
          </HeroCard.Caption>
          <HeroCard.Title
            size={["xSmall", "small", "small", "medium"]}
            testId={`${idPrefix}__title`}
            sx={{ ...lineClamp(10) }} // set this to a higher number like 10 so it never shows ... on the title. Default by Biome is 3
          >
            <MultiLine lines={quest.title} testId={`${idPrefix}__title`} />
          </HeroCard.Title>
          <HeroCard.Description testId={`${idPrefix}__description`}>
            <MultiLineWithBadge
              badgeProps={badgeProps}
              lines={quest.combinedDescription}
              testId={`${idPrefix}__description`}
            />
          </HeroCard.Description>
        </div>

        <div className="flex items-center w-full justify-between gap-2">
          {quest.gemsAmount ? (
            <Badge
              variant={hasCompleted ? "success" : "glass"}
              className="self-center md:py-2"
              endIcon={
                <ImxRewards
                  className={`fill-white size-3 ${hasCompleted ? "fill-black" : "fill-white"}`}
                />
              }
            >
              {quest.gemsAmount}
            </Badge>
          ) : (
            <div />
          )}

          <div className="flex flex-row gap-2 items-center">
            {/* Secondary Actions */}
            {secondaryActions.length > 0 &&
              secondaryActions.map((action, i) => (
                <HeroCard.ButtCon
                  key={action.action}
                  testId={`${idPrefix}__secondary-${i}`}
                  {...regularVariant(action.icon)}
                  size={["small", "small", "small", "medium"]}
                  {...(action.link
                    ? {
                        rc: (
                          <Link
                            href={action.link?.toString()}
                            onClick={(e) => handleButtonClick(action, e)}
                            target="_blank"
                            rel="noreferrer"
                          />
                        ),
                      }
                    : { onClick: (e) => handleButtonClick(action, e) })}
                />
              ))}

            {/* Primary Actions */}
            {primaryActions.length > 0 &&
              primaryActions.map((action) => (
                <HeroCard.ButtCon
                  size={["small", "small", "small", "medium"]}
                  // @ts-ignore
                  icon={
                    action.icon !== "Document"
                      ? "ArrowForward"
                      : regularVariant(action.icon).icon
                  }
                  variant="primary"
                  key={action.action}
                  rc={
                    action.link ? (
                      quest.isImmutablePlayLink() ? (
                        <Link
                          href={action.link}
                          onClick={(e) => handleButtonClick(action, e)}
                          referrerPolicy="same-origin"
                          target="_self"
                        />
                      ) : (
                        <Link
                          href={action.link?.toString()}
                          onClick={(e) => handleButtonClick(action, e)}
                          target="_blank"
                          rel="noreferrer"
                        />
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleButtonClick(action, e)}
                      />
                    )
                  }
                />
              ))}
          </div>
        </div>
      </HeroCard>

      {/* Rules Dialog */}
      {quest.rules || quest.actions.Layer3 ? (
        quest.id === RuleId.NINTENDO_SWITCH_LEADERBOARD_SERIES ? (
          <CrossGameModal
            open={showRules}
            onClose={() => {
              analytics.trackCtaClick({
                control: "Close",
                controlType: "Button",
                screen: "QuestTileModal",
              });
              setShowRules(false);
            }}
          />
        ) : (
          <RulesDialog
            quest={quest}
            isVisible={showRules}
            testId={`${idPrefix}-rules-dialog`}
            onClose={() => {
              analytics.trackCtaClick({
                control: "Close",
                controlType: "Button",
                screen: "QuestTileModal",
              });
              setShowRules(false);
            }}
          />
        )
      ) : null}
    </Box>
  );
};
